import { Pressable, StyleSheet, Text, View } from 'react-native';
import CardsIcon from '../../../../components/Icons/CardsIcon';
import WalletIcon from '../../../../components/Icons/WalletIcon';
import { IThemeContext, useCredentialData, useThemeContext } from '../../../../contexts';
import { animated, IntersectionArgs, useInView } from '@react-spring/web';
import AnimationFactory from '../../../../components/animation/AnimationFactory';
import { useNavigate } from 'react-router-dom';
import {
  getResponsivePadding,
  getFluidFontSize,
  getFluidSpacing,
  getDeviceInfo
} from '../../../../helpers/utils';

interface EmptyStateProps {
  showWelcome: boolean;
  isFirstView: boolean;
}

/**
 *
 *
 * @param {*} { showWelcome, isFirstView }
 * @return {*}
 */
const EmptyState: React.FC<EmptyStateProps> = ({ showWelcome, isFirstView }) => {
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const { loading } = useCredentialData();
  const styles = createCredentialListStyles(theme);
  const deviceInfo = getDeviceInfo();

  const [iconAnim, iconAnimStyle] = useInView(AnimationFactory.getAnimation('fadeUp'), [
    loading
  ] as IntersectionArgs);

  const [textAnim, textAnimStyle] = useInView(AnimationFactory.withDelay('fadeUp', 100), [
    loading
  ] as IntersectionArgs);

  const [buttonAnim, buttonAnimStyle] = useInView(AnimationFactory.withDelay('fadeUp', 200), [
    loading
  ] as IntersectionArgs);

  // Responsive icon size
  const iconSize = deviceInfo.isMobile ? 100 : deviceInfo.isTablet ? 120 : 140;

  return (
    <View style={styles.emptyListStyle}>
      <View style={{ flex: 1, justifyContent: 'flex-end' }}>
        <animated.div ref={iconAnim} style={iconAnimStyle}>
          {showWelcome && isFirstView ? (
            <CardsIcon iconWidth={iconSize} iconHeight={iconSize} />
          ) : (
            <WalletIcon iconWidth={iconSize} iconHeight={iconSize} />
          )}
        </animated.div>
      </View>

      <View>
        <animated.div ref={textAnim} style={textAnimStyle}>
          <View style={styles.emptyListTextStyle}>
            <Text style={styles.emptyListMessageTitleStyle}>
              {showWelcome && isFirstView
                ? 'Welcome to your new digital wallet!'
                : 'Your wallet is empty'}
            </Text>
            <Text style={styles.emptyListMessageSubtitleStyle}>
              {showWelcome && isFirstView
                ? 'You can use this app to receive, store and present your digital credentials!'
                : 'Add new credentials to your wallet by reviewing your credential offers...'}
            </Text>
          </View>
        </animated.div>
      </View>

      <View style={{ flex: 1, justifyContent: 'flex-end' }}>
        <animated.div ref={buttonAnim} style={buttonAnimStyle}>
          <Pressable style={styles.onboardButton} role="button" onPress={() => navigate('/offers')}>
            <Text style={styles.onboardButtonText}>Check Credential Offers</Text>
          </Pressable>
        </animated.div>
      </View>
    </View>
  );
};

export default EmptyState;

const createCredentialListStyles = (
  theme: IThemeContext['theme']
): ReturnType<typeof StyleSheet.create> =>
  StyleSheet.create({
    container: {
      width: '100%',
      minHeight: '100%',
      marginRight: 'auto',
      marginLeft: 'auto'
    },
    listContainer: {
      gap: 0,
      padding: getResponsivePadding(12, 16, 20)
    },
    emptyListStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      width: '100%',
      gap: getFluidSpacing(16, 20, 24),
      height: '100%',
      paddingHorizontal: getResponsivePadding(16, 20, 24),
      margin: 'auto'
    },
    emptyListTextStyle: {
      display: 'flex',
      alignItems: 'center',
      gap: getFluidSpacing(8, 10, 12),
      width: '100%'
    },
    emptyListMessageTitleStyle: {
      fontSize: getFluidFontSize(20, 22, 24, 18, 28),
      fontWeight: '700',
      textAlign: 'center',
      width: '100%'
    },
    emptyListMessageSubtitleStyle: {
      fontSize: getFluidFontSize(14, 16, 18, 12, 20),
      color: theme.textSecondary,
      textAlign: 'center',
      width: '100%',
      paddingHorizontal: getResponsivePadding(8, 12, 16)
    },
    onboardButton: {
      paddingVertical: getResponsivePadding(12, 14, 16),
      paddingHorizontal: getResponsivePadding(20, 24, 28),
      backgroundColor: theme.primary,
      borderRadius: 6,
      color: '#fff',
      textAlign: 'center',
      boxShadow: '1px 2px 4px rgba(41, 45, 50, 0.5)',
      width: 350
    },
    onboardButtonText: {
      textAlign: 'center',
      color: 'inherit',
      fontSize: getFluidFontSize(14, 15, 16, 12, 18),
      fontWeight: '600',
      width: '100%'
    },
    credentialCard: {
      width: '100%',
      boxShadow: '0 -2px 0 #fff',
      borderRadius: 9
    }
  });
